/**
 * Members API
 *
 * Handles club membership and member management.
 */

import { apiGet, apiPost, apiPut, apiDelete } from '../api';

export interface Member {
  user_id: string;
  name: string;
  public_display_name: string;
  email?: string; // Only visible to leaders/owners
  profile_image_url: string | null;
  role: 'owner' | 'leader' | 'moderator' | 'member';
  is_owner: boolean;
}

export interface PaginationMeta {
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface PaginatedMembers {
  members: Member[];
  pagination: PaginationMeta;
}

export interface AddMemberPayload {
  user_id: string;
  role?: 'leader' | 'member';
}

export interface UpdateMemberRolePayload {
  role: 'leader' | 'member';
}

export const membersApi = {
  /**
   * Get members of a club (paginated)
   * GET /clubs/:clubSlug/members?limit=20&offset=0
   * Issue #773: Added pagination support
   */
  getClubMembers: (clubSlug: string, limit = 100, offset = 0) =>
    apiGet<PaginatedMembers>(`/clubs/${clubSlug}/members?limit=${limit}&offset=${offset}`),

  /**
   * Add member to club
   * POST /clubs/:clubSlug/members
   */
  addMember: (clubSlug: string, payload: AddMemberPayload) => 
    apiPost<void>(`/clubs/${clubSlug}/members`, payload),

  /**
   * Update member role
   * PUT /clubs/:clubSlug/members/:userId/role
   */
  updateMemberRole: (clubSlug: string, userId: string, payload: UpdateMemberRolePayload) => 
    apiPut<void>(`/clubs/${clubSlug}/members/${userId}/role`, payload),

  /**
   * Remove member from club
   * DELETE /clubs/:clubSlug/members/:userId
   */
  removeMember: (clubSlug: string, userId: string) => 
    apiDelete<void>(`/clubs/${clubSlug}/members/${userId}`),
};
